import { PLATFORMS, TEMPLATES, templateById } from '@smart-link/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link as RouterLink, createFileRoute, redirect } from '@tanstack/react-router';
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Building2,
  ChevronLeft,
  ChevronRight,
  Copy,
  CopyPlus,
  FileSearch,
  Link2,
  Loader2,
  MoreHorizontal,
  Plus,
  RefreshCw,
  Search,
  Settings2,
  ShieldCheck,
  ShieldX,
  Trash2,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { BillingUnitSelect } from '../components/BillingUnitSelect';
import {
  LogoField,
  appNameLabel,
  appNamePlaceholder,
  logoFieldLabel,
} from '../components/LogoField';
import { PlatformLabel } from '../components/PlatformBadge';
import { TableFilter } from '../components/TableFilter';
import { useToast } from '../components/Toaster';
import { Avatar } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { api, getToken } from '../lib/api';
import { cn } from '../lib/cn';
import { domainOptions, publicLinkUrl, useDomains } from '../lib/domains';
import {
  EMPTY_LINK_FILTERS,
  type LinkFilters,
  UNBOUND_CUSTOMER,
  UNKNOWN_CREATOR,
  UNSPECIFIED_PLATFORM,
  matchesLinkFilters,
} from '../lib/link-filters';

const DEFAULT_DOMAIN = '__default__';
// Radix Select 不允许空字符串 value，用哨兵表示「未指定平台」（筛选与新建表单共用）。
const NO_PLATFORM = '__none__';
const PAGE_SIZE = 20;

interface LinkRow {
  id: string;
  name: string;
  billingUnitId: string | null;
  billingUnitName: string | null;
  status: 'active' | 'paused';
  realUrl: string;
  decoyUrl: string;
  template: string;
  platform: string;
  domain: string;
  appName: string;
  logo: string;
  buttonColor: string;
  rulesOverrides: string;
  createdBy: string;
  createdByName: string;
  createdByAvatar: string;
  createdAt: number;
  updatedAt: number;
}

/** 异步加载的统计：真实(pass)/审核(block)/未验证(pending) 累计 + 今日增量 */
interface LinkStat {
  real: number;
  audit: number;
  pending: number;
  realToday: number;
  auditToday: number;
  pendingToday: number;
}
type StatsMap = Record<string, LinkStat>;

type SortKey = 'created' | 'name' | 'real' | 'audit' | 'today';
type SortDir = 'asc' | 'desc';

export const Route = createFileRoute('/links')({
  beforeLoad: () => {
    if (!getToken()) throw redirect({ to: '/login' });
  },
  component: LinksPage,
});

function fmtDateTime(ts: number): string {
  const d = new Date(ts);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${mm}/${dd} ${hh}:${mi}`;
}

function LinksPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const [showCreate, setShowCreate] = useState(false);

  // toolbar state
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<LinkFilters>(EMPTY_LINK_FILTERS);
  const [deleting, setDeleting] = useState<LinkRow | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>('created');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [page, setPage] = useState(1);

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['links'],
    queryFn: () => api<{ rows: LinkRow[] }>('/api/links'),
    refetchInterval: 30_000,
  });

  // 统计异步加载：表格先渲染，统计到达后填充
  const {
    data: statsResp,
    isLoading: statsLoading,
    error: statsError,
    refetch: refetchStats,
  } = useQuery({
    queryKey: ['links-stats'],
    queryFn: () => api<{ stats: StatsMap }>('/api/links/stats'),
    refetchInterval: 30_000,
  });
  const stats: StatsMap = statsResp?.stats ?? {};

  const { data: domainsData } = useDomains();

  const setDomain = useMutation({
    mutationFn: ({ id, domain }: { id: string; domain: string }) =>
      api(`/api/links/${id}`, { method: 'PUT', body: JSON.stringify({ domain }) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['links'] });
      qc.invalidateQueries({ queryKey: ['billing-link-options'] });
    },
    onError: (e) => toast.error('保存失败', e instanceof Error ? e.message : '未知错误'),
  });

  const setPlatform = useMutation({
    mutationFn: ({ id, platform }: { id: string; platform: string }) =>
      api(`/api/links/${id}`, { method: 'PUT', body: JSON.stringify({ platform }) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['links'] });
      qc.invalidateQueries({ queryKey: ['billing-link-options'] });
    },
    onError: (e) => toast.error('保存失败', e instanceof Error ? e.message : '未知错误'),
  });

  const deleteLink = useMutation({
    mutationFn: (id: string) => api(`/api/links/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['links'] });
      qc.invalidateQueries({ queryKey: ['billing-usage'] });
      qc.invalidateQueries({ queryKey: ['billing-link-options'] });
      qc.invalidateQueries({ queryKey: ['billing-links'] });
      setDeleting(null);
      toast.success('已删除');
    },
  });

  const duplicateLink = useMutation({
    mutationFn: (link: LinkRow) =>
      api<{ id: string }>('/api/links', {
        method: 'POST',
        body: JSON.stringify({
          name: `副本 - ${link.name}`,
          billingUnitId: link.billingUnitId,
          template: link.template,
          platform: link.platform,
          appName: link.appName,
          logo: link.logo,
          buttonColor: link.buttonColor,
          realUrl: link.realUrl,
          decoyUrl: link.decoyUrl,
          rulesOverrides: link.rulesOverrides,
        }),
      }),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['billing-link-options'] });
      qc.invalidateQueries({ queryKey: ['billing-usage'] });
      qc.invalidateQueries({ queryKey: ['billing-links'] });
      qc.invalidateQueries({ queryKey: ['links'] });
      toast.success('复制成功', `已创建副本 /${data.id}`);
    },
    onError: (e) => toast.error('复制失败', e instanceof Error ? e.message : '未知错误'),
  });

  const rows = data?.rows ?? [];
  const filtered = useMemo(
    () =>
      rows.filter((link) =>
        matchesLinkFilters(link, filters, search, stats[link.id], !!statsResp && !statsError),
      ),
    [rows, filters, search, stats, statsResp, statsError],
  );

  const sorted = useMemo(() => {
    const arr = [...filtered];
    const dir = sortDir === 'asc' ? 1 : -1;
    arr.sort((a, b) => {
      const sa = stats[a.id];
      const sb = stats[b.id];
      switch (sortKey) {
        case 'name':
          return dir * a.name.localeCompare(b.name);
        case 'real':
          return dir * ((sa?.real ?? 0) - (sb?.real ?? 0));
        case 'audit':
          return dir * ((sa?.audit ?? 0) - (sb?.audit ?? 0));
        case 'today':
          return (
            dir *
            ((sa?.realToday ?? 0) +
              (sa?.auditToday ?? 0) -
              (sb?.realToday ?? 0) -
              (sb?.auditToday ?? 0))
          );
        default:
          return dir * (a.createdAt - b.createdAt);
      }
    });
    return arr;
  }, [filtered, sortKey, sortDir, stats]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const curPage = Math.min(page, totalPages);
  const paged = useMemo(
    () => sorted.slice((curPage - 1) * PAGE_SIZE, curPage * PAGE_SIZE),
    [sorted, curPage],
  );

  // 搜索/筛选/排序变化时回到第 1 页
  // biome-ignore lint/correctness/useExhaustiveDependencies: 故意以筛选/排序变化为触发，回调只重置页码
  useEffect(() => {
    setPage(1);
  }, [search, filters, sortKey, sortDir]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const customers = [
    ...new Map(
      rows
        .filter((link) => link.billingUnitId)
        .map((link) => [link.billingUnitId!, link.billingUnitName ?? link.billingUnitId!]),
    ).entries(),
  ];
  const creators = [
    ...new Map(
      rows.map((link) => [
        link.createdBy || UNKNOWN_CREATOR,
        link.createdByName || link.createdBy || '未记录',
      ]),
    ).entries(),
  ];
  const filterDefinitions = [
    {
      key: 'customer',
      title: '客户',
      options: [
        { value: UNBOUND_CUSTOMER, label: '未关联' },
        ...customers.map(([value, label]) => ({ value, label })),
      ],
    },
    {
      key: 'platform',
      title: '平台',
      options: [
        ...PLATFORMS.map((item) => ({ value: item.id, label: item.shortLabel })),
        { value: UNSPECIFIED_PLATFORM, label: '未指定' },
      ],
    },
    {
      key: 'template',
      title: '模板',
      options: TEMPLATES.map((item) => ({ value: item.id, label: item.shortLabel })),
    },
    {
      key: 'status',
      title: '状态',
      options: [
        { value: 'active', label: '启用' },
        { value: 'paused', label: '暂停' },
      ],
    },
    {
      key: 'creator',
      title: '创建人',
      options: creators.map(([value, label]) => ({ value, label })),
    },
    {
      key: 'traffic',
      title: '流量',
      options: [
        { value: 'todayActive', label: '今日有量' },
        { value: 'zero', label: '零流量' },
      ],
      disabled: statsLoading || !!statsError,
    },
  ] satisfies {
    key: keyof LinkFilters;
    title: string;
    options: { value: string; label: string }[];
    disabled?: boolean;
  }[];
  const hasFilters = !!search || Object.values(filters).some((values) => values.length > 0);
  const resetFilters = () => {
    setFilters(EMPTY_LINK_FILTERS);
    setSearch('');
  };

  return (
    <div className="mx-auto max-w-[1600px] space-y-5">
      <div className="flex items-center justify-between gap-4">
        <h1 className="flex items-center gap-2.5 text-xl font-semibold tracking-tight">
          <Link2 className="text-muted-foreground size-5" />
          链接
          <span className="text-muted-foreground text-sm font-normal tabular-nums">
            {rows.length}
          </span>
        </h1>
        <Button onClick={() => setShowCreate(true)}>
          <Plus />
          新建链接
        </Button>
      </div>
      {showCreate && (
        <CreateLinkForm
          onCreated={() => {
            setShowCreate(false);
            qc.invalidateQueries({ queryKey: ['links'] });
            qc.invalidateQueries({ queryKey: ['billing-link-options'] });
            qc.invalidateQueries({ queryKey: ['billing-usage'] });
          }}
          onCancel={() => setShowCreate(false)}
        />
      )}
      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative w-full sm:w-52 sm:flex-none">
            <Search className="text-muted-foreground absolute top-2.5 left-3 size-4" />
            <Input
              aria-label="搜索链接"
              placeholder="搜索链接"
              title="名称、短链、域名、URL 或客户"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="h-9 pr-9 pl-9"
            />
            {search && (
              <Button
                variant="ghost"
                size="icon"
                aria-label="清空搜索"
                className="absolute top-0.5 right-0.5 size-8"
                onClick={() => setSearch('')}
              >
                <X />
              </Button>
            )}
          </div>
          {filterDefinitions.map((filter) => (
            <TableFilter
              key={filter.key}
              title={filter.title}
              options={filter.options}
              value={filters[filter.key]}
              disabled={filter.disabled}
              onChange={(value) => setFilters((previous) => ({ ...previous, [filter.key]: value }))}
            />
          ))}
          {hasFilters && (
            <Button variant="ghost" size="sm" className="h-8 px-2 text-xs" onClick={resetFilters}>
              重置
              <X className="size-3.5" />
            </Button>
          )}
          <div className="ml-auto flex items-center gap-1.5">
            <Select
              value={`${sortKey}:${sortDir}`}
              onValueChange={(value) => {
                const [key, dir] = value.split(':');
                setSortKey(key as SortKey);
                setSortDir(dir as SortDir);
              }}
            >
              <SelectTrigger aria-label="链接排序" size="sm" className="h-8 w-28 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="created:desc">最新创建</SelectItem>
                <SelectItem value="created:asc">最早创建</SelectItem>
                <SelectItem value="name:asc">名称 A–Z</SelectItem>
                <SelectItem value="name:desc">名称 Z–A</SelectItem>
                <SelectItem value="real:desc">累计放行 ↓</SelectItem>
                <SelectItem value="real:asc">累计放行 ↑</SelectItem>
                <SelectItem value="audit:desc">累计拦截 ↓</SelectItem>
                <SelectItem value="audit:asc">累计拦截 ↑</SelectItem>
                <SelectItem value="today:desc">今日流量 ↓</SelectItem>
                <SelectItem value="today:asc">今日流量 ↑</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="ghost"
              size="icon"
              className="size-8"
              aria-label="刷新链接"
              title="刷新"
              disabled={isFetching}
              onClick={() => {
                refetch();
                refetchStats();
              }}
            >
              <RefreshCw className={isFetching ? 'animate-spin' : ''} />
            </Button>
          </div>
        </div>
      </div>
      {error && (
        <div role="alert" className="flex items-center gap-3 rounded-lg border p-4 text-sm">
          <span className="text-destructive">
            {error instanceof Error ? error.message : '加载失败'}
          </span>
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            重试
          </Button>
        </div>
      )}
      {statsError && (
        <div role="alert" className="flex items-center gap-3 text-xs text-muted-foreground">
          统计加载失败
          <Button variant="ghost" size="sm" onClick={() => refetchStats()}>
            重试
          </Button>
        </div>
      )}
      <div className="overflow-hidden rounded-xl border">
        <Table className="min-w-[900px] table-fixed">
          <TableHeader className="bg-muted/25">
            <TableRow className="hover:bg-transparent">
              <TableHead className="w-[28%]">
                <Button
                  variant="ghost"
                  size="sm"
                  className="-ml-2 h-8 gap-1.5 px-2 text-xs"
                  onClick={() => toggleSort('name')}
                >
                  链接
                  {sortKey === 'name' ? (
                    sortDir === 'asc' ? (
                      <ArrowUp className="size-3" />
                    ) : (
                      <ArrowDown className="size-3" />
                    )
                  ) : (
                    <ArrowUpDown className="size-3 opacity-40" />
                  )}
                </Button>
              </TableHead>
              <TableHead className="w-[23%] text-xs">跳转地址</TableHead>
              <TableHead className="w-[10%] text-right text-xs">
                放行<span className="block text-[10px] font-normal opacity-60">今日 / 累计</span>
              </TableHead>
              <TableHead className="w-[10%] text-right text-xs">
                拦截<span className="block text-[10px] font-normal opacity-60">今日 / 累计</span>
              </TableHead>
              <TableHead className="w-[9%] text-right text-xs">
                未验证<span className="block text-[10px] font-normal opacity-60">今日 / 累计</span>
              </TableHead>
              <TableHead
                className="w-[8%] text-right text-xs"
                title="累计拦截与未验证占全部访问的比例"
              >
                拦截率
              </TableHead>
              <TableHead className="w-[12%] text-right text-xs">操作</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading
              ? [1, 2, 3, 4, 5].map((key) => (
                  <TableRow key={key}>
                    {[1, 2, 3, 4, 5, 6, 7].map((cell) => (
                      <TableCell key={cell} className="py-5">
                        <Skeleton className="h-7 w-full" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              : paged.map((link) => {
                  const stat = stats[link.id];
                  const shortUrl = publicLinkUrl(link.id, link.domain, domainsData);
                  return (
                    <TableRow key={link.id} className="group">
                      <TableCell className="py-4">
                        <div className="flex min-w-0 items-center gap-2">
                          <RouterLink
                            to="/link/$id"
                            params={{ id: link.id }}
                            title={link.name}
                            className="min-w-0 flex-1 truncate font-medium hover:underline"
                          >
                            {link.name}
                          </RouterLink>
                          {link.status === 'paused' ? (
                            <Badge variant="secondary" className="shrink-0 text-[10px]">
                              暂停
                            </Badge>
                          ) : (
                            link.platform && <PlatformLabel id={link.platform} />
                          )}
                        </div>
                        <div className="text-muted-foreground mt-1.5 flex min-w-0 items-center gap-1.5 text-xs">
                          <code className="shrink-0 font-mono">/{link.id}</code>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="size-5 shrink-0"
                            aria-label={`复制${link.name}的公开链接`}
                            title={shortUrl}
                            onClick={() => {
                              navigator.clipboard.writeText(shortUrl);
                              toast.success('已复制');
                            }}
                          >
                            <Copy className="size-3" />
                          </Button>
                          {link.billingUnitName && (
                            <RouterLink
                              to="/billing"
                              title={link.billingUnitName}
                              className="ml-1 flex min-w-0 items-center gap-1 border-l pl-2 hover:text-foreground"
                            >
                              <Building2 className="size-3 shrink-0" />
                              <span className="truncate">{link.billingUnitName}</span>
                            </RouterLink>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1.5">
                          <UrlRow
                            label="放行"
                            url={link.realUrl}
                            onCopy={() => {
                              navigator.clipboard.writeText(link.realUrl);
                              toast.success('已复制');
                            }}
                          />
                          <UrlRow
                            label="审核"
                            url={link.decoyUrl}
                            onCopy={() => {
                              navigator.clipboard.writeText(link.decoyUrl);
                              toast.success('已复制');
                            }}
                          />
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <MetricCell
                          today={stat?.realToday}
                          total={stat?.real}
                          loading={statsLoading}
                          tone="text-emerald-600 dark:text-emerald-400"
                        />
                      </TableCell>
                      <TableCell className="text-right">
                        <MetricCell
                          today={stat?.auditToday}
                          total={stat?.audit}
                          loading={statsLoading}
                        />
                      </TableCell>
                      <TableCell className="text-right">
                        <MetricCell
                          today={stat?.pendingToday}
                          total={stat?.pending}
                          loading={statsLoading}
                        />
                      </TableCell>
                      <TableCell className="text-right">
                        <BlockRateCell s={stat} />
                      </TableCell>
                      <TableCell className="px-2">
                        <div className="flex items-center justify-end gap-0.5">
                          <Button
                            asChild
                            variant="ghost"
                            size="icon"
                            className="text-muted-foreground size-7"
                            title="审计"
                          >
                            <RouterLink
                              aria-label={`查看${link.name}的审计`}
                              to="/audits/$linkId"
                              params={{ linkId: link.id }}
                            >
                              <FileSearch className="size-3.5" />
                            </RouterLink>
                          </Button>
                          <Button
                            asChild
                            variant="ghost"
                            size="icon"
                            className="text-muted-foreground size-7"
                            title="配置"
                          >
                            <RouterLink
                              aria-label={`配置${link.name}`}
                              to="/link/$id"
                              params={{ id: link.id }}
                            >
                              <Settings2 className="size-3.5" />
                            </RouterLink>
                          </Button>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="text-muted-foreground size-7"
                                aria-label={`更多操作：${link.name}`}
                                title="更多"
                              >
                                <MoreHorizontal className="size-3.5" />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent align="end" className="w-72 p-3">
                              <div className="space-y-3 border-b pb-3">
                                <div className="flex items-center justify-between gap-2 text-xs">
                                  <span className="text-muted-foreground">模板</span>
                                  <span>
                                    {templateById(link.template)?.shortLabel ?? link.template}
                                  </span>
                                </div>
                                <div className="space-y-1.5">
                                  <Label
                                    htmlFor={`domain-${link.id}`}
                                    className="text-muted-foreground text-xs"
                                  >
                                    域名
                                  </Label>
                                  <Select
                                    value={link.domain || DEFAULT_DOMAIN}
                                    disabled={setDomain.isPending}
                                    onValueChange={(value) =>
                                      setDomain.mutate({
                                        id: link.id,
                                        domain: value === DEFAULT_DOMAIN ? '' : value,
                                      })
                                    }
                                  >
                                    <SelectTrigger
                                      id={`domain-${link.id}`}
                                      size="sm"
                                      className="w-full"
                                    >
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {domainOptions(domainsData).map((option) => (
                                        <SelectItem
                                          key={option.value || DEFAULT_DOMAIN}
                                          value={option.value || DEFAULT_DOMAIN}
                                          disabled={option.disabled}
                                        >
                                          {option.label}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div className="space-y-1.5">
                                  <Label
                                    htmlFor={`platform-${link.id}`}
                                    className="text-muted-foreground text-xs"
                                  >
                                    平台
                                  </Label>
                                  <Select
                                    value={link.platform || NO_PLATFORM}
                                    disabled={setPlatform.isPending}
                                    onValueChange={(value) =>
                                      setPlatform.mutate({
                                        id: link.id,
                                        platform: value === NO_PLATFORM ? '' : value,
                                      })
                                    }
                                  >
                                    <SelectTrigger
                                      id={`platform-${link.id}`}
                                      size="sm"
                                      className="w-full"
                                    >
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value={NO_PLATFORM}>未指定</SelectItem>
                                      {PLATFORMS.map((item) => (
                                        <SelectItem key={item.id} value={item.id}>
                                          <PlatformLabel id={item.id} />
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div className="text-muted-foreground flex items-center justify-between gap-2 text-xs">
                                  <span className="flex min-w-0 items-center gap-1.5">
                                    <Avatar
                                      src={link.createdByAvatar}
                                      name={link.createdByName || '未知'}
                                    />
                                    <span className="truncate">{link.createdByName || '—'}</span>
                                  </span>
                                  <span className="shrink-0">{fmtDateTime(link.createdAt)}</span>
                                </div>
                              </div>
                              <div className="pt-2">
                                <MenuButton
                                  icon={<CopyPlus className="size-3.5" />}
                                  disabled={duplicateLink.isPending}
                                  onClick={() => duplicateLink.mutate(link)}
                                >
                                  创建副本
                                </MenuButton>
                                <MenuButton
                                  icon={<Trash2 className="size-3.5" />}
                                  destructive
                                  onClick={() => {
                                    deleteLink.reset();
                                    setDeleting(link);
                                  }}
                                >
                                  删除
                                </MenuButton>
                              </div>
                            </PopoverContent>
                          </Popover>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
            {!isLoading && sorted.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="h-48 text-center">
                  <Link2 className="text-muted-foreground/40 mx-auto mb-3 size-6" />
                  <p className="text-muted-foreground text-sm">
                    {error ? '加载失败' : rows.length === 0 ? '暂无链接' : '无匹配链接'}
                  </p>
                  {rows.length > 0 && (
                    <Button variant="ghost" size="sm" className="mt-2" onClick={resetFilters}>
                      清空筛选
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        {!isLoading && (
          <div className="text-muted-foreground flex items-center justify-between gap-3 border-t px-4 py-2 text-xs">
            <span className="tabular-nums">
              {sorted.length
                ? `${(curPage - 1) * PAGE_SIZE + 1}–${Math.min(curPage * PAGE_SIZE, sorted.length)} / ${sorted.length}`
                : '0'}
              {sorted.length !== rows.length && (
                <span className="ml-2 opacity-60">共 {rows.length}</span>
              )}
            </span>
            <div className="flex items-center gap-2 tabular-nums">
              <Button
                variant="ghost"
                size="icon"
                className="size-7"
                aria-label="上一页链接"
                disabled={curPage <= 1}
                onClick={() => setPage(curPage - 1)}
              >
                <ChevronLeft />
              </Button>
              <span>
                {curPage} / {totalPages}
              </span>
              <Button
                variant="ghost"
                size="icon"
                className="size-7"
                aria-label="下一页链接"
                disabled={curPage >= totalPages}
                onClick={() => setPage(curPage + 1)}
              >
                <ChevronRight />
              </Button>
            </div>
          </div>
        )}
      </div>
      <Dialog
        open={!!deleting}
        onOpenChange={(open) => {
          if (!open && !deleteLink.isPending) {
            setDeleting(null);
            deleteLink.reset();
          }
        }}
      >
        <DialogContent aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>删除链接？</DialogTitle>
          </DialogHeader>
          <p className="truncate text-sm text-muted-foreground">{deleting?.name}</p>
          <code className="text-muted-foreground text-xs">/{deleting?.id}</code>
          {deleteLink.error && (
            <p role="alert" className="text-destructive text-sm">
              {deleteLink.error instanceof Error ? deleteLink.error.message : '删除失败'}
            </p>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              disabled={deleteLink.isPending}
              onClick={() => {
                setDeleting(null);
                deleteLink.reset();
              }}
            >
              取消
            </Button>
            <Button
              variant="destructive"
              disabled={deleteLink.isPending}
              onClick={() => deleting && deleteLink.mutate(deleting.id)}
            >
              {deleteLink.isPending && <Loader2 className="animate-spin" />}删除
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** 拦截率单元（异步）：没放行率 = (audit + pending) / (real + audit + pending)。
 *  pending（未验证：着陆但 verify 未触发）也是花钱买的广告到访，没能到达真实落地页，
 *  故归入「没放行」一侧；分母为全部广告到访。= 1 − 通过率。按高低着色。 */
function BlockRateCell({ s }: { s?: LinkStat }) {
  if (!s) return <span className="text-muted-foreground text-xs">—</span>;
  const total = s.real + s.audit + s.pending;
  if (total === 0) return <span className="text-muted-foreground text-xs">—</span>;
  const rate = ((s.audit + s.pending) / total) * 100;
  const HIGH = 85; // 红：没放行率过高
  const WARN = 70; // 黄
  const tone =
    rate >= HIGH
      ? 'text-destructive'
      : rate >= WARN
        ? 'text-amber-600 dark:text-amber-400'
        : 'text-emerald-600 dark:text-emerald-400';
  return (
    <span className={cn('font-mono text-sm font-semibold tabular-nums', tone)}>
      {rate.toFixed(0)}%
    </span>
  );
}

function MetricCell({
  today,
  total,
  loading,
  tone,
}: { today?: number; total?: number; loading: boolean; tone?: string }) {
  if (loading && today === undefined) return <Skeleton className="ml-auto h-8 w-10" />;
  if (today === undefined) return <span className="text-muted-foreground">—</span>;
  return (
    <div className="tabular-nums">
      <div className={cn('text-sm font-medium', today > 0 ? tone : 'text-muted-foreground')}>
        {today.toLocaleString()}
      </div>
      <div className="mt-1 text-xs text-muted-foreground/65" title="累计">
        {total?.toLocaleString() ?? '—'}
      </div>
    </div>
  );
}

function MenuButton({
  icon,
  children,
  onClick,
  destructive,
  disabled,
}: {
  icon: React.ReactNode;
  children: React.ReactNode;
  onClick: () => void;
  destructive?: boolean;
  disabled?: boolean;
}) {
  return (
    <Button
      variant="ghost"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'h-8 w-full justify-start px-2 text-xs',
        destructive && 'text-destructive hover:text-destructive hover:bg-destructive/10',
      )}
    >
      {icon}
      {children}
    </Button>
  );
}

function UrlRow({
  label,
  url,
  onCopy,
}: { label: '放行' | '审核'; url: string; onCopy: () => void }) {
  const Icon = label === '放行' ? ShieldCheck : ShieldX;
  return (
    <div className="flex min-w-0 items-center gap-2 text-xs">
      <Icon
        aria-label={label}
        className={cn(
          'size-3.5 shrink-0',
          label === '放行' ? 'text-emerald-500/80' : 'text-amber-500/80',
        )}
      />
      {url ? (
        <>
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            className="min-w-0 flex-1 truncate text-muted-foreground hover:text-foreground hover:underline"
            title={`${label} · ${url}`}
          >
            {url.replace(/^https?:\/\//, '')}
          </a>
          <Button
            variant="ghost"
            size="icon"
            className="size-5 shrink-0 text-muted-foreground opacity-0 focus-visible:opacity-100 group-hover:opacity-100"
            aria-label={`复制${label}地址`}
            onClick={onCopy}
          >
            <Copy className="size-3" />
          </Button>
        </>
      ) : (
        <span className="text-muted-foreground">—</span>
      )}
    </div>
  );
}

function CreateLinkForm({ onCreated, onCancel }: { onCreated: () => void; onCancel: () => void }) {
  const toast = useToast();
  const [form, setForm] = useState({
    name: '',
    billingUnitId: null as string | null,
    template: 'download' as string,
    platform: '' as string,
    appName: '',
    logo: '',
    buttonColor: '',
    realUrl: '',
    decoyUrl: '',
  });

  const create = useMutation({
    mutationFn: () =>
      api<{ id: string }>('/api/links', {
        method: 'POST',
        body: JSON.stringify({
          name: form.name,
          billingUnitId: form.billingUnitId,
          template: form.template,
          platform: form.platform,
          appName: form.appName,
          logo: form.logo,
          buttonColor: form.buttonColor,
          realUrl: form.realUrl,
          decoyUrl: form.decoyUrl,
        }),
      }),
    onSuccess: (data) => {
      toast.success('创建成功', `链接 /${data.id} 已创建`);
      onCreated();
    },
    onError: (e) => toast.error('创建失败', e instanceof Error ? e.message : '未知错误'),
  });

  const tpl = templateById(form.template);
  const defaultBtnColor = tpl?.defaultButtonColor ?? '#00BCD4';

  return (
    <Card className="mb-4 gap-4 p-5">
      <h2 className="text-sm font-semibold">新建链接</h2>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label className="text-muted-foreground text-xs">名称</Label>
          <Input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="TikTok SG 双 11 活动"
          />
        </div>
        <BillingUnitSelect
          value={form.billingUnitId}
          onChange={(billingUnitId) => setForm({ ...form, billingUnitId })}
        />
        <div className="space-y-1.5">
          <Label className="text-muted-foreground text-xs">落地页模板</Label>
          <Select value={form.template} onValueChange={(v) => setForm({ ...form, template: v })}>
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TEMPLATES.filter((tmpl) => !tmpl.disabled).map((tmpl) => (
                <SelectItem key={tmpl.id} value={tmpl.id}>
                  {tmpl.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-muted-foreground text-xs">投放平台 (仅筛选用)</Label>
          <Select
            value={form.platform || NO_PLATFORM}
            onValueChange={(v) => setForm({ ...form, platform: v === NO_PLATFORM ? '' : v })}
          >
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NO_PLATFORM}>未指定</SelectItem>
              {PLATFORMS.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {tpl?.hasAppName && (
          <>
            <div className="space-y-1.5">
              <Label className="text-muted-foreground text-xs">{appNameLabel(tpl)}</Label>
              <Input
                value={form.appName}
                onChange={(e) => setForm({ ...form, appName: e.target.value })}
                placeholder={appNamePlaceholder(tpl)}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-muted-foreground text-xs">{logoFieldLabel(tpl)}</Label>
              <LogoField
                tpl={tpl}
                value={form.logo}
                onChange={(logo) => setForm({ ...form, logo })}
              />
            </div>
            {tpl?.hasButtonColor && (
              <div className="space-y-1.5">
                <Label className="text-muted-foreground text-xs">
                  按钮颜色 (留空默认 {defaultBtnColor})
                </Label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={form.buttonColor || defaultBtnColor}
                    onChange={(e) => setForm({ ...form, buttonColor: e.target.value })}
                    className="h-9 w-12 shrink-0 cursor-pointer rounded border border-zinc-200 p-0.5 dark:border-zinc-700"
                  />
                  <Input
                    value={form.buttonColor}
                    onChange={(e) => setForm({ ...form, buttonColor: e.target.value })}
                    placeholder={defaultBtnColor}
                  />
                </div>
              </div>
            )}
          </>
        )}
        <div className="space-y-1.5">
          <Label className="text-muted-foreground text-xs">真实页 (通过后跳转)</Label>
          <Input
            type="url"
            value={form.realUrl}
            onChange={(e) => setForm({ ...form, realUrl: e.target.value })}
            placeholder="https://your-real-landing.com/..."
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-muted-foreground text-xs">安全页 (拦截后跳转)</Label>
          <Input
            type="url"
            value={form.decoyUrl}
            onChange={(e) => setForm({ ...form, decoyUrl: e.target.value })}
            placeholder="https://shopee.sg/..."
          />
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel}>
          取消
        </Button>
        <Button
          onClick={() => create.mutate()}
          disabled={!form.name || !form.realUrl || !form.decoyUrl || create.isPending}
        >
          {create.isPending ? '创建中…' : '创建'}
        </Button>
      </div>
    </Card>
  );
}
